/**
 * v2 dual-run seam — shared by the v1 AppShell (toggle item) and the
 * v2 shell (Back to classic). Lives in config/ because v1 must never
 * import from `@/v2/*` (eslint guard) yet both sides need the same
 * route mapping + preference storage.
 *
 * The preference is honored ONLY on document-load entry at exactly
 * `/` (see BootUiGate in routes/index.tsx) — explicit URLs are never
 * rewritten.
 */

export const UI_PREF_KEY = 'relay.ui';

export type UiPref = 'v1' | 'v2';

export function setUiPref(pref: UiPref): void {
  try {
    localStorage.setItem(UI_PREF_KEY, pref);
  } catch {
    // Private mode / storage denied — the toggle still navigates.
  }
}

export function getUiPref(): UiPref | null {
  try {
    const v = localStorage.getItem(UI_PREF_KEY);
    return v === 'v1' || v === 'v2' ? v : null;
  } catch {
    return null;
  }
}

export function clearUiPref(): void {
  try {
    localStorage.removeItem(UI_PREF_KEY);
  } catch {
    // ignore
  }
}

/**
 * v1 ↔ v2 path pairs, longest-prefix-first so `/contracts/templates`
 * wins over `/contracts`. v2 renames two domains per the design
 * handoff: workers → candidates, vacancies → jobs.
 */
const PATH_PAIRS: ReadonlyArray<readonly [v1: string, v2: string]> = [
  ['/workers/new', '/beta/candidates/new'],
  ['/workers', '/beta/candidates'],
  ['/vacancies/new', '/beta/jobs/new'],
  ['/vacancies', '/beta/jobs'],
  ['/employees', '/beta/employees'],
  ['/leave', '/beta/leave'],
  ['/companies/new', '/beta/companies/new'],
  ['/companies', '/beta/companies'],
  ['/contracts/templates', '/beta/contracts/templates'],
  ['/contracts/new', '/beta/contracts/new'],
  ['/contracts', '/beta/contracts'],
  ['/dashboard', '/beta/dashboard'],
  ['/reports', '/beta/reports'],
  ['/settings', '/beta/settings'],
];

function stripQuery(pathname: string): string {
  const q = pathname.indexOf('?');
  return q === -1 ? pathname : pathname.slice(0, q);
}

/** Map a v1 pathname to its /beta equivalent; fallback `/beta`. */
export function v1PathToV2(pathname: string): string {
  const path = stripQuery(pathname);
  for (const [v1] of PATH_PAIRS) {
    if (path === v1 || path.startsWith(`${v1}/`)) {
      // Section landing only — deep params (ids) don't transfer
      // across the rename boundary; land on the list instead.
      return PATH_PAIRS.find(([p]) => p === v1)![1];
    }
  }
  return '/beta';
}

/** Map a /beta pathname back to its v1 equivalent; fallback `/`. */
export function v2PathToV1(pathname: string): string {
  const path = stripQuery(pathname);
  for (const [v1, v2] of PATH_PAIRS) {
    if (path === v2 || path.startsWith(`${v2}/`)) {
      return v1;
    }
  }
  return '/';
}
